import { useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { toast } from "sonner";
import { Trash, Warning, ShieldCheck, Eye } from "@phosphor-icons/react";

const ENTITIES = [
  { key: "matches", label: "Marcadores / Partidos", hint: "Historial de marcadores, sets y validaciones" },
  { key: "retas", label: "Retas", hint: "Retas multi, retas 1v1, pizarrones y rondas" },
  { key: "tournaments", label: "Torneos", hint: "Torneos y sus brackets" },
  { key: "leagues", label: "Ligas", hint: "Ligas y temporadas" },
  { key: "court_rentals", label: "Rentas de Cancha", hint: "Reservas y pagos de canchas" },
  { key: "clinics", label: "Clínicas", hint: "Clases y clínicas registradas" },
  { key: "trainings", label: "Entrenamientos", hint: "Bitácoras de entrenamiento" },
  { key: "payments", label: "Pagos", hint: "Transacciones Stripe y registros internos" },
  { key: "payment_transactions", label: "Transacciones de Pago", hint: "Sesiones Stripe Checkout" },
  { key: "dyd_withdrawals", label: "Retiros DYD", hint: "Solicitudes de retiro via Stripe Connect" },
  { key: "dyd_transactions", label: "Movimientos DYD", hint: "Earn/spend/transfer/bet del wallet DYD" },
  { key: "dyd_bets", label: "Apuestas DYD", hint: "Apuestas internas entre usuarios" },
  { key: "dyd_shop_orders", label: "Órdenes Tienda DYD", hint: "Compras realizadas en la tienda" },
  { key: "invitations", label: "Invitaciones", hint: "Invitaciones enviadas a jugadores" },
  { key: "announcements", label: "Anuncios", hint: "Banners globales del sitio" },
  { key: "streamings", label: "Streamings", hint: "Sesiones y grabaciones live" },
  { key: "chat_messages", label: "Chat", hint: "Mensajes de chat entre usuarios" },
  { key: "dyd_chat_messages", label: "DYD Coach Chat (legacy)", hint: "Historial del asistente IA DYD" },
  { key: "dyd_messages", label: "DYD Messages", hint: "Mensajes con el coach DYD" },
  { key: "trivia_results", label: "Trivia", hint: "Puntajes de trivia" },
  { key: "atari_scores", label: "Atari", hint: "Records del videojuego Atari" },
  { key: "memorama_scores", label: "Memorama", hint: "Records del juego memorama" },
  { key: "post_comments", label: "Comentarios de Posts", hint: "Comentarios en feed social" },
  { key: "shop_comments", label: "Comentarios de Tienda", hint: "Reviews de productos" },
  { key: "wearable_data", label: "Datos Wearables", hint: "Apple Watch / Suunto sync data" },
  { key: "user_sessions", label: "Sesiones de Usuario", hint: "Logins y device tracking" },
  { key: "notifications", label: "Notificaciones", hint: "Notificaciones in-app" },
  { key: "audit_logs", label: "Audit Logs", hint: "Bitácora de auditoría administrativa" },
];

const STATUS_OPTIONS_BY_ENTITY = {
  matches: ["all", "validado", "finalizado", "pendiente_validacion", "en_curso"],
  retas: ["all", "finalizada", "aceptada", "rechazada", "pendiente"],
  court_rentals: ["all", "reservada", "confirmada", "completada", "cancelada"],
  tournaments: ["all", "abierto", "en_curso", "finalizado", "cancelado"],
  clinics: ["all", "abierta", "cerrada"],
  trainings: ["all"],
  payments: ["all", "succeeded", "pending", "failed", "refunded"],
  invitations: ["all", "pendiente", "aceptada", "rechazada"],
  streamings: ["all", "live", "ended"],
  chat_messages: ["all"],
  dyd_chat_messages: ["all"],
  trivia_results: ["all"],
  atari_scores: ["all"],
  notifications: ["all"],
  audit_logs: ["all"],
};

export default function AdminCleanup() {
  const { user } = useAuth();
  const [selected, setSelected] = useState({});
  const [wipeAll, setWipeAll] = useState(false);
  const [beforeDate, setBeforeDate] = useState("");
  const [status, setStatus] = useState("all");
  const [recalc, setRecalc] = useState(true);
  const [confirmText, setConfirmText] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  if (user?.role !== "admin") {
    return (
      <div className="p-8">
        <Alert variant="destructive" data-testid="admin-cleanup-no-access">
          <Warning className="h-4 w-4" />
          <AlertTitle>Acceso restringido</AlertTitle>
          <AlertDescription>Solo el administrador puede limpiar historiales.</AlertDescription>
        </Alert>
      </div>
    );
  }

  const selectedKeys = Object.keys(selected).filter((k) => selected[k]);

  const toggleEntity = (k) => setSelected((s) => ({ ...s, [k]: !s[k] }));

  const buildPayload = () => ({
    entities: selectedKeys,
    wipe_all: wipeAll,
    before_date: beforeDate || null,
    status: status || "all",
    recalculate_stats: recalc,
  });

  const runPreview = async () => {
    if (!selectedKeys.length) {
      toast.error("Selecciona al menos una entidad");
      return;
    }
    setBusy(true);
    setPreview(null);
    try {
      const { data } = await api.post("/admin/cleanup/preview", buildPayload());
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error en vista previa");
    } finally {
      setBusy(false);
    }
  };

  const runCleanup = async () => {
    if (!selectedKeys.length) {
      toast.error("Selecciona al menos una entidad");
      return;
    }
    if (confirmText !== "BORRAR") {
      toast.error("Debes escribir BORRAR para confirmar");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/cleanup", { ...buildPayload(), confirm: "BORRAR" });
      const total = Object.values(data.deleted || {}).reduce((a, b) => a + b, 0);
      toast.success(`${total} registros eliminados${data.stats_recalculated ? " y estadisticas recalculadas" : ""}`);
      setPreview({ counts: data.deleted, wipe_all: wipeAll, before_date: beforeDate, status });
      setConfirmText("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al limpiar");
    } finally {
      setBusy(false);
    }
  };

  const canExecute = selectedKeys.length > 0 && confirmText === "BORRAR" && !busy;

  return (
    <div className="p-4 md:p-8 max-w-5xl" data-testid="admin-cleanup-page">
      <div className="mb-6">
        <h1 className="font-['Barlow_Condensed'] font-black text-4xl uppercase tracking-tight text-white">
          Limpiar <span className="text-[#CCFF00]">Historiales</span>
        </h1>
        <p className="text-gray-400 text-sm mt-2">
          Elimina registros antiguos combinando entidades, rango de fechas y estado. Las estadisticas se recalculan automaticamente al eliminar partidos o retas.
        </p>
      </div>

      <Alert className="mb-6 border-yellow-500/40 bg-yellow-500/5" data-testid="admin-cleanup-warning">
        <Warning className="h-4 w-4 text-yellow-400" />
        <AlertTitle className="text-yellow-300">Accion irreversible</AlertTitle>
        <AlertDescription className="text-yellow-200/80 text-xs">
          Los registros eliminados no se pueden recuperar. Usa la vista previa antes de ejecutar.
        </AlertDescription>
      </Alert>

      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <div className="bg-[#141414] border border-white/10 p-5">
          <div className="text-xs uppercase tracking-widest text-gray-500 mb-3">1. Entidades</div>
          <div className="space-y-3">
            {ENTITIES.map((e) => (
              <label key={e.key} className="flex items-start gap-3 cursor-pointer" data-testid={`cleanup-entity-${e.key}`}>
                <Checkbox checked={!!selected[e.key]} onCheckedChange={() => toggleEntity(e.key)} />
                <div>
                  <div className="text-sm font-semibold text-white">{e.label}</div>
                  <div className="text-xs text-gray-500">{e.hint}</div>
                </div>
              </label>
            ))}
          </div>
        </div>

        <div className="bg-[#141414] border border-white/10 p-5 space-y-4">
          <div className="text-xs uppercase tracking-widest text-gray-500 mb-1">2. Criterios</div>

          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-semibold text-white">Borrado total</div>
              <div className="text-xs text-gray-500">Ignora fecha y estado, elimina todo</div>
            </div>
            <Switch checked={wipeAll} onCheckedChange={setWipeAll} data-testid="cleanup-wipe-all" />
          </div>

          <div className={wipeAll ? "opacity-40 pointer-events-none" : ""}>
            <Label className="text-xs text-gray-400 uppercase">Eliminar registros antes de</Label>
            <Input
              type="date"
              value={beforeDate}
              onChange={(e) => setBeforeDate(e.target.value)}
              className="bg-[#0A0A0A] border-white/10 mt-1"
              data-testid="cleanup-before-date"
            />
          </div>

          <div className={wipeAll ? "opacity-40 pointer-events-none" : ""}>
            <Label className="text-xs text-gray-400 uppercase">Estado</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="bg-[#0A0A0A] border-white/10 mt-1" data-testid="cleanup-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Cualquier estado</SelectItem>
                <SelectItem value="validado">validado</SelectItem>
                <SelectItem value="finalizado">finalizado</SelectItem>
                <SelectItem value="finalizada">finalizada (reta)</SelectItem>
                <SelectItem value="cancelado">cancelado</SelectItem>
                <SelectItem value="cancelada">cancelada</SelectItem>
                <SelectItem value="rechazada">rechazada</SelectItem>
                <SelectItem value="completada">completada</SelectItem>
                <SelectItem value="confirmada">confirmada</SelectItem>
                <SelectItem value="en_curso">en_curso</SelectItem>
                <SelectItem value="abierto">abierto</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[11px] text-gray-500 mt-1">El filtro aplica al campo "status" de cada entidad. Notificaciones solo aceptan fecha.</p>
          </div>

          <div className="flex items-center justify-between pt-2 border-t border-white/5">
            <div>
              <div className="text-sm font-semibold text-white">Recalcular estadisticas</div>
              <div className="text-xs text-gray-500">Tras eliminar partidos/retas</div>
            </div>
            <Switch checked={recalc} onCheckedChange={setRecalc} data-testid="cleanup-recalc-stats" />
          </div>
        </div>
      </div>

      <div className="bg-[#141414] border border-white/10 p-5 mb-6">
        <div className="text-xs uppercase tracking-widest text-gray-500 mb-3">3. Vista previa</div>
        <div className="flex items-center gap-3">
          <Button onClick={runPreview} disabled={busy || !selectedKeys.length} variant="outline" className="border-[#CCFF00]/40 text-[#CCFF00] hover:bg-[#CCFF00]/10" data-testid="cleanup-preview-btn">
            <Eye className="mr-2" /> Ver cuanto se eliminaria
          </Button>
          {preview && (
            <div className="flex flex-wrap gap-2" data-testid="cleanup-preview-results">
              {Object.entries(preview.counts || {}).map(([k, v]) => (
                <span key={k} className="px-3 py-1.5 bg-[#0A0A0A] border border-white/10 text-xs">
                  <span className="text-gray-400">{k}:</span>{" "}
                  <span className="font-bold text-[#CCFF00]">{v}</span>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="bg-[#141414] border border-red-500/30 p-5">
        <div className="text-xs uppercase tracking-widest text-red-400 mb-3 flex items-center gap-2">
          <ShieldCheck /> 4. Confirmacion final
        </div>
        <Label className="text-xs text-gray-400 uppercase">Escribe BORRAR para habilitar la eliminacion</Label>
        <Input
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          placeholder="BORRAR"
          className="bg-[#0A0A0A] border-red-500/30 mt-1 uppercase tracking-widest font-bold"
          data-testid="cleanup-confirm-input"
        />
        <Button
          onClick={runCleanup}
          disabled={!canExecute}
          className="mt-4 bg-red-500 hover:bg-red-600 text-white font-bold uppercase tracking-widest disabled:opacity-30"
          data-testid="cleanup-execute-btn"
        >
          <Trash className="mr-2" /> Ejecutar limpieza
        </Button>
      </div>
    </div>
  );
}
